import { defineStore } from "pinia";

import { getCompanyId } from "@/utils/userUtils";
import {
  trailerTypeLabel,
  trailerTypeOptions,
  vehicleTypeLabel,
  vehicleTypeOptions,
} from "@/utils/vehicleTypeUtils";

import vehiclesApi from "@/services/api/vehiclesApi";

export const useVehicleStore = defineStore("vehicle", {
  state: () => ({
    vehicles: [],

    companyId: getCompanyId(),

    searchQuery: "",
    vehicleTypeFilter: "",
    edoVehicleRoleFilter: "",
    fleetParkFilter: "",

    pagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
    },

    dataIsLoading: false,

    vehicleTypeOptions: vehicleTypeOptions(),

    trailerTypeOptions: trailerTypeOptions(),
  }),

  getters: {
    vehicleTypeLabels: (state) =>
      Object.fromEntries(
        state.vehicleTypeOptions.map((opt) => [opt.value, opt.label])
      ),

    trailerTypeLabels: (state) =>
      Object.fromEntries(
        state.trailerTypeOptions.map((opt) => [opt.value, opt.label])
      ),
  },

  actions: {
    formatVehicleType(type) {
      return vehicleTypeLabel(type);
    },

    formatTrailerType(type) {
      return trailerTypeLabel(type);
    },

    async fetchVehicles(payload = {}) {
      this.searchQuery = payload.search || "";
      this.vehicleTypeFilter = payload.vehicleType || "";
      this.edoVehicleRoleFilter = payload.edoVehicleRole || "";
      this.fleetParkFilter = payload.fleetParkId || "";
      return await this.getVehiclesRequest(1);
    },

    async loadMoreVehicles() {
      if (this.dataIsLoading || !this.pagination.next) {
        return;
      }

      const nextPage = this.pagination.page + 1;
      return await this.getVehiclesRequest(nextPage);
    },

    async ensureVehiclesLoaded(vehicleIds) {
      if (!vehicleIds || vehicleIds.length === 0) return;

      const missingIds = vehicleIds.filter(
        (id) =>
          id != null &&
          !this.vehicles.find((v) => String(v.id) === String(id))
      );

      if (missingIds.length === 0) return;

      try {
        const results = await Promise.all(
          missingIds.map((id) =>
            vehiclesApi
              .show(this.companyId, id)
              .then((res) => res.data.result)
              .catch(() => null)
          )
        );

        const loaded = results.filter(Boolean);
        if (loaded.length > 0) {
          this.vehicles = [...this.vehicles, ...loaded];
        }
      } catch (error) {
        console.error(
          "Ошибка при точечной загрузке транспорта:",
          error
        );
      }
    },

    async fetchVehicleSelectItem(vehicleId) {
      const exists = this.vehicles.find(
        (vehicle) => String(vehicle.id) === String(vehicleId)
      );
      if (exists) return exists;

      const response = await vehiclesApi.show(this.companyId, vehicleId);
      return response.data.result;
    },

    async createVehicle(payload) {
      const response = await vehiclesApi.create(
        this.companyId,
        payload
      );

      return response.data.result;
    },

    async updateVehicle(vehicleId, payload) {
      const response = await vehiclesApi.update(
        this.companyId,
        vehicleId,
        payload
      );

      return response.data.result;
    },

    replaceVehicle(vehicle) {
      if (!vehicle?.id) return;

      const index = this.vehicles.findIndex(
        (item) => String(item.id) === String(vehicle.id)
      );

      if (index === -1) {
        this.vehicles = [vehicle, ...this.vehicles];
        return;
      }

      this.vehicles.splice(index, 1, vehicle);
    },

    async getVehiclesRequest(page) {
      const pageNumber = parseInt(page) || 1;

      try {
        this.dataIsLoading = true;

        const params = { page: pageNumber };
        params["q[enabled]"] = "true";

        if (this.searchQuery) params["q[search]"] = this.searchQuery;
        if (this.vehicleTypeFilter)
          params["q[vehicle_type]"] = this.vehicleTypeFilter;
        if (this.edoVehicleRoleFilter)
          params["q[edo_vehicle_role]"] = this.edoVehicleRoleFilter;
        if (this.fleetParkFilter)
          params["q[fleet_park_id]"] = this.fleetParkFilter;

        const response = await vehiclesApi.index(
          this.companyId,
          params
        );

        if (pageNumber === 1) {
          this.vehicles = response.data.result;
        } else {
          this.vehicles = [...this.vehicles, ...response.data.result];
        }

        const meta = response.data.pagination_meta;
        this.pagination = { ...meta };
      } catch (error) {
        console.error("Ошибка при загрузке транспорта:", error);
      } finally {
        this.dataIsLoading = false;
      }
    },

    clearVehicles() {
      this.vehicles = [];
      this.searchQuery = "";
      this.vehicleTypeFilter = "";
      this.edoVehicleRoleFilter = "";
      this.fleetParkFilter = "";
      this.pagination = {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      };
    },
  },
});
